import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
// Navigate retained for RequireAuth redirect to /login below.
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/layout.js';
import { LoginPage } from './pages/login.js';
import { ForgotPasswordPage } from './pages/forgot-password.js';
import { ResetPasswordPage } from './pages/reset-password.js';
import { SettingsPage } from './pages/settings.js';
import { AdminUsersPage } from './pages/admin-users.js';
import { AdminPermissionsPage } from './pages/admin-permissions.js';
import { AdminPbxApiKeysPage } from './pages/admin-pbx-api-keys.js';
import { AdminKitePagesPage } from './pages/admin-kite-pages.js';
import { AdminKitePageEditPage } from './pages/admin-kite-page-edit.js';
import { DashboardPage } from './pages/dashboard.js';
import { CustomerPage } from './pages/customer.js';
import { CustomerConfigPage } from './pages/customer-config.js';
import { ContactProfilesPage } from './pages/contact-profiles.js';
import { MessageTemplatesPage } from './pages/message-templates.js';
import { EventsPage } from './pages/events.js';
import { CampaignsPage } from './pages/campaigns.js';
import { NewContactProfilePage } from './pages/contact-profile-new.js';
import { ContactProfilesImportPage } from './pages/contact-profiles-import.js';
import { NewMessageTemplatePage } from './pages/message-template-new.js';
import { NewEventPage } from './pages/event-new.js';
import { NewCampaignPage } from './pages/campaign-new.js';
import { EditContactProfilePage } from './pages/contact-profile-edit.js';
import { EditMessageTemplatePage } from './pages/message-template-edit.js';
import { EditEventPage } from './pages/event-edit.js';
import { EditCampaignPage } from './pages/campaign-edit.js';
import { EventDetailPage } from './pages/event-detail.js';
import { CampaignDetailPage } from './pages/campaign-detail.js';
import { CalendarPage } from './pages/calendar.js';
import { WebhooksPage } from './pages/webhooks.js';
import { NewWebhookPage } from './pages/webhook-new.js';
import { WebhookDetailPage } from './pages/webhook-detail.js';
import { EmailAccountsPage } from './pages/email-accounts.js';
import { SmsProvidersPage } from './pages/sms-providers.js';
import { NewEmailAccountPage } from './pages/email-account-new.js';
import { EmailAccountDetailPage } from './pages/email-account-detail.js';
import { DiagnosticsPage } from './pages/diagnostics.js';
import { ServiceProfilesPage } from './pages/service-profiles.js';
import { NewServiceProfilePage } from './pages/service-profile-new.js';
import { EditServiceProfilePage } from './pages/service-profile-edit.js';
import { ThemeProvider } from './lib/theme.js';
import { useIsAuthenticated } from './lib/auth-store.js';
import { ErrorBoundary } from './components/error-boundary.js';
import { NotFoundPage } from './pages/not-found.js';
import { AdminShell } from './components/admin-shell.js';
import { AdminIndexPage } from './pages/admin.js';
import { TrainingHomePage } from './pages/training-home.js';
import { AdminTrainingPage } from './pages/admin-training.js';
import { AdminTrainingEditPage } from './pages/admin-training-edit.js';
import { AdminTrainingRosterPage } from './pages/admin-training-roster.js';
import { TrainingModulePage } from './pages/training-module.js';
import { TrainingQuizPage } from './pages/training-quiz.js';
import { LearnerModulePage } from './pages/learn-module.js';
import { LearnerQuizPage } from './pages/learn-quiz.js';
import { LearnerExpiredPage } from './pages/learn-expired.js';
import { LearnerOptedOutPage } from './pages/learn-opted-out.js';

/** Wrap an admin page so the tabbed shell stays consistent across them. */
function Admin({ children }: { children: JSX.Element }): JSX.Element {
  return <AdminShell>{children}</AdminShell>;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      retry: 1,
    },
  },
});

/** Path prefix the SPA is hosted under. "/xsc" when Wildix's nginx
 *  proxies /xsc/* to our App on the PBX; "" (root) in dev. */
const BASE_PATH = (import.meta.env.VITE_BASE_PATH as string | undefined)?.replace(/\/$/, '') ?? '';

export function App(): JSX.Element {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={BASE_PATH || undefined}>
          <ErrorBoundary>
          <Layout>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
              <Route path="/admin" element={<RequireAuth><AdminIndexPage /></RequireAuth>} />
              <Route path="/admin/users" element={<RequireAuth><Admin><AdminUsersPage /></Admin></RequireAuth>} />
              <Route path="/admin/permissions" element={<RequireAuth><Admin><AdminPermissionsPage /></Admin></RequireAuth>} />
              <Route path="/admin/pbx-api-keys" element={<RequireAuth><Admin><AdminPbxApiKeysPage /></Admin></RequireAuth>} />
              <Route path="/admin/training" element={<RequireAuth><Admin><AdminTrainingPage /></Admin></RequireAuth>} />
              <Route path="/admin/training/roster" element={<RequireAuth><Admin><AdminTrainingRosterPage /></Admin></RequireAuth>} />
              <Route path="/admin/training/:id" element={<RequireAuth><Admin><AdminTrainingEditPage /></Admin></RequireAuth>} />
              <Route path="/admin/kite-pages" element={<RequireAuth><Admin><AdminKitePagesPage /></Admin></RequireAuth>} />
              <Route path="/admin/kite-pages/:id" element={<RequireAuth><Admin><AdminKitePageEditPage /></Admin></RequireAuth>} />
              {/* Canonical /admin/<tab> aliases. Legacy URLs above still resolve
                  the same components, so existing bookmarks / outbound links
                  keep working. */}
              {/* Canonical /admin/<tab> routes render the same component as the
                  legacy paths so admin navigation can stay on the /admin
                  namespace. Legacy paths below remain as backwards-compat. */}
              <Route path="/admin/templates" element={<RequireAuth><Admin><MessageTemplatesPage /></Admin></RequireAuth>} />
              <Route path="/admin/webhooks" element={<RequireAuth><Admin><WebhooksPage /></Admin></RequireAuth>} />
              <Route path="/admin/email" element={<RequireAuth><Admin><EmailAccountsPage /></Admin></RequireAuth>} />
              <Route path="/admin/sms" element={<RequireAuth><Admin><SmsProvidersPage /></Admin></RequireAuth>} />
              <Route path="/admin/customer" element={<RequireAuth><Admin><CustomerPage /></Admin></RequireAuth>} />
              <Route path="/" element={<RequireAuth><DashboardPage /></RequireAuth>} />
              <Route path="/calendar" element={<RequireAuth><CalendarPage /></RequireAuth>} />
              <Route path="/customer" element={<RequireAuth><Admin><CustomerPage /></Admin></RequireAuth>} />
              <Route
                path="/customer/config"
                element={<RequireAuth><Admin><CustomerConfigPage /></Admin></RequireAuth>}
              />
              <Route
                path="/contact-profiles"
                element={<RequireAuth><ContactProfilesPage /></RequireAuth>}
              />
              <Route
                path="/contact-profiles/new"
                element={<RequireAuth><NewContactProfilePage /></RequireAuth>}
              />
              <Route
                path="/contact-profiles/import"
                element={<RequireAuth><ContactProfilesImportPage /></RequireAuth>}
              />
              <Route
                path="/contact-profiles/:id/edit"
                element={<RequireAuth><EditContactProfilePage /></RequireAuth>}
              />
              <Route
                path="/message-templates"
                element={<RequireAuth><Admin><MessageTemplatesPage /></Admin></RequireAuth>}
              />
              <Route
                path="/message-templates/new"
                element={<RequireAuth><Admin><NewMessageTemplatePage /></Admin></RequireAuth>}
              />
              <Route
                path="/message-templates/:id/edit"
                element={<RequireAuth><Admin><EditMessageTemplatePage /></Admin></RequireAuth>}
              />
              <Route path="/events" element={<RequireAuth><EventsPage /></RequireAuth>} />
              <Route path="/events/new" element={<RequireAuth><NewEventPage /></RequireAuth>} />
              <Route path="/events/:id/edit" element={<RequireAuth><EditEventPage /></RequireAuth>} />
              <Route path="/events/:id" element={<RequireAuth><EventDetailPage /></RequireAuth>} />
              <Route path="/campaigns" element={<RequireAuth><CampaignsPage /></RequireAuth>} />
              <Route path="/campaigns/new" element={<RequireAuth><NewCampaignPage /></RequireAuth>} />
              <Route path="/campaigns/:id/edit" element={<RequireAuth><EditCampaignPage /></RequireAuth>} />
              <Route path="/campaigns/:id" element={<RequireAuth><CampaignDetailPage /></RequireAuth>} />
              <Route path="/webhooks" element={<RequireAuth><Admin><WebhooksPage /></Admin></RequireAuth>} />
              <Route path="/webhooks/new" element={<RequireAuth><Admin><NewWebhookPage /></Admin></RequireAuth>} />
              <Route path="/webhooks/:id" element={<RequireAuth><Admin><WebhookDetailPage /></Admin></RequireAuth>} />
              <Route path="/sms-providers" element={<RequireAuth><Admin><SmsProvidersPage /></Admin></RequireAuth>} />
              <Route path="/email-accounts" element={<RequireAuth><Admin><EmailAccountsPage /></Admin></RequireAuth>} />
              <Route path="/email-accounts/new" element={<RequireAuth><Admin><NewEmailAccountPage /></Admin></RequireAuth>} />
              <Route path="/email-accounts/:id" element={<RequireAuth><Admin><EmailAccountDetailPage /></Admin></RequireAuth>} />
              <Route path="/service-profiles" element={<RequireAuth><ServiceProfilesPage /></RequireAuth>} />
              <Route path="/service-profiles/new" element={<RequireAuth><NewServiceProfilePage /></RequireAuth>} />
              <Route path="/service-profiles/:id/edit" element={<RequireAuth><EditServiceProfilePage /></RequireAuth>} />
              {/* Task #17 — Training & Certifications (learner-facing) */}
              <Route path="/training" element={<RequireAuth><TrainingHomePage /></RequireAuth>} />
              <Route path="/training/:moduleId" element={<RequireAuth><TrainingModulePage /></RequireAuth>} />
              <Route path="/training/:moduleId/quiz" element={<RequireAuth><TrainingQuizPage /></RequireAuth>} />
              {/* Task #19 — Customer-facing learner portal (cookie-authed, no
                  staff login). The /learn/:token endpoint is server-side; these
                  SPA routes render after the redirect lands on /learn/module. */}
              <Route path="/learn/module" element={<LearnerModulePage />} />
              <Route path="/learn/quiz" element={<LearnerQuizPage />} />
              <Route path="/learn/expired" element={<LearnerExpiredPage />} />
              <Route path="/learn/opted-out" element={<LearnerOptedOutPage />} />
              {/* Hidden operator page — reachable by URL only, not in nav */}
              <Route path="/diagnostics" element={<RequireAuth><DiagnosticsPage /></RequireAuth>} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Layout>
          </ErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const authed = useIsAuthenticated();
  return authed ? children : <Navigate to="/login" replace />;
}
